import { Link } from 'react-router-dom';
import { Home, ArrowLeft, Crown } from 'lucide-react';
import { useEffect } from 'react';
import SEO from '../components/shared/SEO';

/**
 * 404 Not Found page — gentle redirect path back into the app.
 * Critical for SEO: prevents blank pages on stale URLs (e.g. old Google index entries).
 */
const NotFoundPage = () => {
  useEffect(() => {
    // Auto-clean any broken service worker that might be serving stale content
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.active?.postMessage({ type: 'UNREGISTER' });
      }).catch(() => {});
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-12">
      <SEO
        title="Page Not Found | Queen B Logistics"
        description="The page you're looking for has moved or no longer exists."
      />
      <div className="max-w-lg w-full text-center">
        <Crown className="w-16 h-16 text-[#D4AF37] mx-auto mb-6 opacity-60" />
        <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-[0.3em] mb-3">
          Lost in the Hive
        </p>
        <h1 className="font-['Barlow_Condensed'] text-6xl sm:text-8xl font-black text-white mb-2 tracking-tight">
          404
        </h1>
        <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-bold text-white uppercase tracking-wide mb-4">
          This Page Has Moved
        </h2>
        <p className="text-white/60 mb-8 leading-relaxed">
          The link you followed may be outdated or this page no longer exists.
          Let's get you back on the right path.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors"
            data-testid="not-found-home-btn"
          >
            <Home className="w-4 h-4" />
            Return Home
          </Link>
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-white/20 text-white hover:bg-white/5 transition-colors"
            data-testid="not-found-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="text-white/40 text-xs uppercase tracking-widest mb-3">
            Popular Destinations
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Link to="/build" className="text-[#D4AF37] hover:text-[#F5D47B] text-sm">Build</Link>
            <span className="text-white/20">·</span>
            <Link to="/protect" className="text-[#D4AF37] hover:text-[#F5D47B] text-sm">Protect</Link>
            <span className="text-white/20">·</span>
            <Link to="/grow" className="text-[#D4AF37] hover:text-[#F5D47B] text-sm">Grow</Link>
            <span className="text-white/20">·</span>
            <Link to="/academy" className="text-[#D4AF37] hover:text-[#F5D47B] text-sm">Academy</Link>
            <span className="text-white/20">·</span>
            <Link to="/intake" className="text-[#D4AF37] hover:text-[#F5D47B] text-sm">Apply</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
